import rvApi from "@/rvapi";
import { defineStore } from "pinia";
import constant from "@/constant";
import type {
  ApiTagTree,
  ApiTagTheme,
  ApiTagCategory,
  CreateCategoryParams,
  UpdateCategoryParams,
  TagManagementNotification,
} from "@/types/tagManagement";

const getInitialNotificationObj = (): TagManagementNotification => ({
  show: false,
  status: "",
  message: "",
});

export const useTagManagementStore = defineStore("tagManagement", {
  state: () => ({
    tagTree: null as ApiTagTree | null,
    notificationObj: getInitialNotificationObj() as TagManagementNotification,
  }),
  getters: {
    themes: (state): ApiTagTheme[] => state.tagTree?.themes ?? [],
    getThemeCategories:
      (state) =>
      (themeId: number): ApiTagCategory[] => {
        const theme = state.tagTree?.themes.find((t) => t.themeId === themeId);
        return theme?.categories ?? [];
      },
    getDominantColorOptions(): ApiTagCategory[] {
      const visualQualityCategories = this.getThemeCategories(
        constant.TAG_THEME_IDS.VISUAL_QUALITY,
      );
      const dominantColorCategory = visualQualityCategories.find((category) =>
        category.enCategoryName.startsWith(
          constant.TAG_CATEGORY_NAME_PREFIXES.DOMINANT_COLOR,
        ),
      );
      return dominantColorCategory?.children ?? [];
    },
    getLanguageCategoryOptions(): ApiTagCategory[] {
      const languageCategories = this.getThemeCategories(
        constant.TAG_THEME_IDS.LANGUAGE,
      );
      const languageCategory = languageCategories.find((category) =>
        category.enCategoryName.startsWith(
          constant.TAG_CATEGORY_NAME_PREFIXES.LANGUAGE,
        ),
      );
      return languageCategory?.children ?? [];
    },
  },
  actions: {
    async getTagTree() {
      this.tagTree = await rvApi.getTagManagementTree();
    },
    async createCategory(params: CreateCategoryParams) {
      try {
        await rvApi.createTagCategory({
          svCategoryName: params.swedishName,
          enCategoryName: params.englishName,
          themeId: params.themeId,
          parentCategoryId: params.parentCategoryId,
          tags: [
            { language: "english", tags: params.englishTags },
            { language: "swedish", tags: params.swedishTags },
          ],
          sortOrder: 1,
          hidden: false,
          visibleFlags: {
            isStickerCollection: params.isStickerCollection,
            isStickerIndividual: params.isStickerIndividual,
          },
        });
        await this.getTagTree();
        this.populateNotificationObj({
          show: true,
          status: "success",
          message: "Category created successfully",
        });
        setTimeout(() => {
          this.resetNotification();
        }, 4000);
      } catch {
        this.populateNotificationObj({
          show: true,
          status: "error",
          message: "There was an error creating the category",
        });
        setTimeout(() => {
          this.resetNotification();
        }, 4000);
      }
    },
    async updateCategory(params: UpdateCategoryParams) {
      try {
        await rvApi.updateTagCategory(params.categoryId, {
          svCategoryName: params.swedishName,
          enCategoryName: params.englishName,
          tags: [
            { language: "english", tags: params.englishTags },
            { language: "swedish", tags: params.swedishTags },
          ],
          visibleFlags: {
            isStickerCollection: params.isStickerCollection,
            isStickerIndividual: params.isStickerIndividual,
          },
        });
        await this.getTagTree();
        this.populateNotificationObj({
          show: true,
          status: "success",
          message: "Category updated successfully",
        });
        setTimeout(() => {
          this.resetNotification();
        }, 4000);
      } catch {
        this.populateNotificationObj({
          show: true,
          status: "error",
          message: "There was an error updating the category",
        });
        setTimeout(() => {
          this.resetNotification();
        }, 4000);
      }
    },
    async deleteCategory(categoryId: number) {
      try {
        await rvApi.deleteTagCategory(categoryId);
        await this.getTagTree();
        this.populateNotificationObj({
          show: true,
          status: "success",
          message: "Category deleted successfully",
        });
        setTimeout(() => {
          this.resetNotification();
        }, 4000);
      } catch {
        this.populateNotificationObj({
          show: true,
          status: "error",
          message: "There was an error deleting the category",
        });
        setTimeout(() => {
          this.resetNotification();
        }, 4000);
      }
    },
    populateNotificationObj({
      show,
      status,
      message,
    }: TagManagementNotification) {
      this.notificationObj.show = show;
      this.notificationObj.status = status;
      this.notificationObj.message = message;
    },
    resetNotification() {
      this.notificationObj.show = false;
      this.notificationObj.status = "";
      this.notificationObj.message = "";
    },
  },
});
